import * as Sentry from "@sentry/react";
import { supabase } from "./supabase";
import { Task, SubTask } from "../types";

/** The next insert of this row must not create a second calendar event. */
export async function suppressCalendarSync(
  entity: "task" | "subtask",
  id: string,
  userId: string,
): Promise<void> {
  const { error } = await supabase.from("calendar_sync_suppress").insert({
    entity,
    entity_id: id,
    user_id: userId,
  });
  if (error) Sentry.captureException(error);
}

/**
 * Move a Google Calendar event's mapping row from one entity type to the
 * other (task ↔ subtask) without recreating the event. Used when converting
 * between task and sub-task so the user keeps the exact same calendar entry.
 * Returns ok=true on success or when there was no mapping to move (which is
 * a success — the user wasn't synced); ok=false on any error so the caller
 * can abort the conversion before touching the original.
 */
export async function rekeyCalendarMapping(
  from: { entity: "task" | "subtask"; id: string },
  to: {
    entity: "task" | "subtask";
    id: string;
    parentTaskId?: string;
    fields: Partial<Task | SubTask> & { id: string };
  },
  timezone: string,
): Promise<{ ok: boolean }> {
  try {
    const { error } = await supabase.functions.invoke("google-calendar-sync", {
      body: {
        action: "rekey-mapping",
        from_entity: from.entity,
        from_id: from.id,
        to_entity: to.entity,
        to_id: to.id,
        parent_task_id: to.parentTaskId,
        task: {
          id: to.fields.id,
          title: to.fields.title,
          scheduled_date: to.fields.scheduled_date,
          scheduled_time: to.fields.scheduled_time,
          duration: to.fields.duration,
          completed: to.fields.completed,
        },
        timezone,
      },
    });
    if (error) {
      Sentry.captureException(error);
      return { ok: false };
    }
    return { ok: true };
  } catch (err) {
    Sentry.captureException(err);
    return { ok: false };
  }
}
